/**
 * useProrationPreview — fetch a proration preview for a plan change.
 *
 * in-app-checkout Phase 3 Session 3.5.
 *
 * Calls `POST /api/billing/proration-preview` which hits Stripe's
 * `invoices.createPreview` with `proration_behavior: 'always_invoice'` and
 * returns the amounts that would be charged today and on the next renewal.
 *
 * Design:
 *   - `useQuery` (not `useMutation`) because the preview is idempotent and
 *     the caller wants reactive refetching when `targetTierId` changes.
 *   - Disabled when `targetTierId` is undefined — the caller controls when
 *     to fetch (e.g. only after the user selects a plan).
 *   - `staleTime: 0` — proration amounts depend on the exact current
 *     timestamp; always refetch to avoid showing stale figures.
 *
 * Wire shape returned by `POST /api/billing/proration-preview`:
 *   {
 *     amount_due_cents: number,       // integer cents owed today
 *     next_invoice_total_cents: number, // integer cents on next renewal
 *     proration_date: number,         // Unix epoch seconds
 *   }
 * All amounts are in integer cents (USD).
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type {
  ProrationPreviewRequest,
  ProrationPreviewResponse,
} from '@arcanum-hq/shared-types';

interface ProrationPreviewEnvelope {
  data: ProrationPreviewResponse;
}

export function useProrationPreview(targetTierId: string | undefined) {
  const query = useQuery<ProrationPreviewResponse>({
    queryKey: queryKeys.billing.prorationPreview(targetTierId),
    queryFn: async () => {
      // `enabled: !!targetTierId` guarantees targetTierId is a non-empty string
      // before this queryFn runs — the non-null assertion is safe here.
      const body: ProrationPreviewRequest = { tier_id: targetTierId! };
      const envelope = await api.post<ProrationPreviewEnvelope>(
        '/api/billing/proration-preview',
        body
      );
      return envelope.data;
    },
    enabled: !!targetTierId,
    // Proration amounts are time-sensitive — never serve stale data.
    staleTime: 0,
    // Never auto-retry — a failed preview is a hard stop, not a transient error.
    retry: false,
  });

  return {
    preview: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
